// Parse a markdown body into an ArticleBlock[]. Shared by:
//   - the first-boot seed in app/server/lib/seed.ts (writes block-shape rows)
//   - the one-shot migration in app/scripts/migrate-articles-to-blocks.ts
//     (converts existing markdown rows when migration 0012 runs)
//
// Conservative by design: anything we can't classify lands as a `text` block
// with the original markdown verbatim, which react-markdown re-parses on
// render. Worst case is "one giant text block" — still renders correctly.
//
// Only recognises three classifications (matches the bundled content shape):
//   - Lines beginning with `## ` or `### ` → heading block (level 2 or 3)
//   - Fenced code blocks ``` ```lang ... ``` ``` → code block
//   - Everything else → text block (markdown preserved verbatim)
//
// Image, embed, callout, author detection is intentionally out of scope —
// bundled content has none, and the admin editor handles those variants
// natively via typed forms (T4.7).

import type { ArticleBlock } from '@/schemas/articleBlock';

const HEADING_PATTERN = /^(#{2,3})\s+(.+?)\s*$/;
const FENCE_OPEN_PATTERN = /^```([\w-]*)\s*$/;
const FENCE_CLOSE_PATTERN = /^```\s*$/;

interface PendingText {
  lines: string[];
}

function flushText(pending: PendingText, out: ArticleBlock[]): void {
  if (pending.lines.length === 0) return;
  // Trim both leading and trailing blank lines — they don't affect
  // markdown rendering but make the block body's storage shape less
  // surprising (and let test fixtures use the obvious string literal).
  while (pending.lines.length > 0 && pending.lines.at(-1)?.trim() === '') {
    pending.lines.pop();
  }
  while (pending.lines.length > 0 && pending.lines[0]?.trim() === '') {
    pending.lines.shift();
  }
  if (pending.lines.length === 0) return;
  out.push({ type: 'text', body: pending.lines.join('\n') });
  pending.lines = [];
}

export function markdownToBlocks(md: string): ArticleBlock[] {
  if (typeof md !== 'string' || md.trim() === '') return [];

  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: ArticleBlock[] = [];
  const pending: PendingText = { lines: [] };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block: collect until the closing fence (or end of input).
    const fenceMatch = line.match(FENCE_OPEN_PATTERN);
    if (fenceMatch) {
      flushText(pending, out);
      const lang = fenceMatch[1] || undefined;
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE_CLOSE_PATTERN.test(lines[i])) {
        codeLines.push(lines[i]);
        i += 1;
      }
      // Skip the closing fence (if present)
      if (i < lines.length) i += 1;
      out.push({ type: 'code', ...(lang ? { lang } : {}), body: codeLines.join('\n') });
      continue;
    }

    // ## / ### heading
    const headingMatch = line.match(HEADING_PATTERN);
    if (headingMatch) {
      flushText(pending, out);
      const level = headingMatch[1].length === 2 ? 2 : 3;
      out.push({ type: 'heading', level, text: headingMatch[2] });
      i += 1;
      continue;
    }

    pending.lines.push(line);
    i += 1;
  }

  flushText(pending, out);
  return out;
}
